import {
  isDemandLevel,
  levelValueOf,
  type DemandLevelKey,
  type PlanningLevelRow,
  UNCATEGORIZED,
} from "~/utils/demandHierarchy"

/**
 * The blending half of the level sales trend: node rates, the mix-effect reconciliation and the
 * per-variant collapse, pure over plain rows. The scan is `demand2/shared/levelSalesTrend.ts`.
 */

export type LevelSalesWindow = {
  /** `date_trunc('week', asOf)`; year 1 ends the day before. */
  anchorWeek: string
  year1Start: string
  year1End: string
  year2Start: string
  year2End: string
  /**
   * Short of `year1End` means the window isn't full: a still-loading final week reads as a
   * collapse in demand.
   */
  maxSaleDate: string | null
}

export type LevelSalesTrend = {
  /** Equals the level's baseline, which makes `Σ appliedᵢ × baselineᵢ` land on `year1 × pct`. */
  year1: number
  /** A disclosure beside the rate; nothing sizes off it. */
  discountYear1: number
  year2: number
  /** `(year1 / year2 - 1) * 100`, or null with no prior year. */
  pct: number | null
  /**
   * `pct` after `correctionRatio`: the one to APPLY when `pctByVariant` has no entry. `pct` itself
   * measures the past and is the wrong multiplicand for a year-1 baseline.
   */
  inheritedPct: number | null
  /**
   * Ends INCLUSIVE. Read back from the scan's `date_trunc`, not recomputed in JS: the week helpers
   * walk local time, so a JS-derived label can name a different week.
   */
  window: LevelSalesWindow
  /** Empty when no hierarchy is configured. */
  nodes: NodeSalesTrend[]
  unsupportedLevels: string[]
  /**
   * Share of its own MOVEMENT each node gives up so node rates net to `pct` (a mix effect): `pct`
   * is base-weighted (Laspeyres) but the rates multiply a year-1 baseline, which needs
   * current-weighted (Paasche). Scaled by movement, not volume, so no node crosses zero; divided by
   * the SUM of movements, not the net effect, which near-cancels and explodes.
   */
  correctionRatio: number
  /**
   * Weighted by where the variant's year-1 demand sat. Empty when `pct` is null; an absent variant
   * falls back to `inheritedPct`.
   */
  pctByVariant: Record<string, number>
  /** False past `MAX_BREAKDOWN_NODES`; otherwise unsold combinations are zero-filled. */
  crossProductComplete: boolean
}

// A three-level hierarchy on a market-heavy tenant can multiply into thousands of nodes.
const MAX_BREAKDOWN_NODES = 2000

/**
 * Nested: `["MAIN", "Ecom"]` is Ecom WITHIN MAIN.
 */
export type NodeSalesTrend = {
  levelKey: string
  path: string[]
  label: string
  year1: number
  year2: number
  /** The node's OWN trend; null with no prior-window business. */
  pct: number | null
  /**
   * `pct`, or the nearest ancestor's with no prior year. Separate from `pct` so an "own growth"
   * column can keep the inherited nodes, whose units the reconciliation counts.
   */
  carriedPct: number | null
  /** `carriedPct` after `correctionRatio`; same sign, always. */
  appliedPct: number | null
}

/**
 * Sums the scan nodes that are one hierarchy node (a value on one level, across the cross
 * product). `appliedPct` is the reconciled rate where exactly one scan node matches, else the
 * plain rate.
 */
export const sumNodeTrend = (
  nodes: readonly NodeSalesTrend[],
  node: { groupKey: string; groupValue: string },
): { year1: number; year2: number; pct: number | null; appliedPct: number | null } => {
  const own = nodes.filter(
    (n) => n.levelKey === node.groupKey && (n.path[n.path.length - 1] ?? "") === node.groupValue,
  )
  const year1 = own.reduce((sum, n) => sum + n.year1, 0)
  const year2 = own.reduce((sum, n) => sum + n.year2, 0)
  const pct = year2 > 0 ? (year1 / year2 - 1) * 100 : null
  return { year1, year2, pct, appliedPct: own.length === 1 ? (own[0]!.appliedPct ?? pct) : pct }
}

/** Already at the hierarchy's cross-product grain. */
export type LevelSalesTrendRow = PlanningLevelRow & {
  variantId: string
  year1: number
  discountYear1: number
  year2: number
  maxDate: string | null
}

/**
 * Unreadable level keys are reported, never dropped: every row would fall into one bucket that
 * looks like a breakdown (`updateDemandSettings` validates only `z.string()`). Shared with the scan
 * so its GROUP BY and the blend agree on the axes.
 */
export function splitSupportedLevels(levelKeys: string[]): {
  supported: DemandLevelKey[]
  unsupportedLevels: string[]
} {
  const supported = levelKeys.filter(isDemandLevel)
  return {
    supported,
    unsupportedLevels: levelKeys.filter((k) => !isDemandLevel(k)),
  }
}

const trendPct = (year1: number, year2: number): number | null =>
  year2 > 0 ? (year1 / year2 - 1) * 100 : null

// Names contain spaces ("D2C E-COM"), so a space-joined key could collide.
const PATH_SEP = "\u0000"

/**
 * `maxSaleDate` is derived from the rows, not taken from the caller. Generic over the window so the
 * season trend can pass its own shape; nothing else of it is read.
 */
export function computeLevelSalesTrend<W extends { maxSaleDate: string | null }>(
  rows: readonly LevelSalesTrendRow[],
  /** Empty = the level total only. */
  levelKeys: string[],
  window: W,
): Omit<LevelSalesTrend, "window"> & { window: W } {
  const { supported, unsupportedLevels } = splitSupportedLevels(levelKeys)
  const wantVariant = supported.length > 0

  let year1 = 0
  let discountYear1 = 0
  let year2 = 0
  // ISO `YYYY-MM-DD` sorts lexicographically: no parsing, no timezone.
  let maxSaleDate: string | null = null
  for (const r of rows) {
    year1 += r.year1
    discountYear1 += r.discountYear1
    year2 += r.year2
    if (r.maxDate != null && (maxSaleDate == null || r.maxDate > maxSaleDate))
      maxSaleDate = r.maxDate
  }

  // Every PREFIX of the path, so level 2 is measured within its level-1 parent.
  const byPath = new Map<string, { levelKey: string; path: string[]; y1: number; y2: number }>()
  for (const r of rows) {
    const values = supported.map((levelKey) => levelValueOf(r, levelKey))
    for (let depth = 0; depth < values.length; depth++) {
      const path = values.slice(0, depth + 1)
      const key = path.join(PATH_SEP)
      const acc = byPath.get(key) ?? { levelKey: supported[depth]!, path, y1: 0, y2: 0 }
      acc.y1 += r.year1
      acc.y2 += r.year2
      byPath.set(key, acc)
    }
  }

  // Zero-fill unsold combinations: absence reads as "unknown", zero as "no business". Values come
  // from what the level uses, not a dimension table.
  const valuesByLevel = supported.map((levelKey) => {
    const seen = new Set<string>()
    for (const r of rows) seen.add(levelValueOf(r, levelKey))
    return [...seen]
  })
  // Bounded, and reported via `crossProductComplete` when it bites.
  let projected = 0
  let running = 1
  for (const values of valuesByLevel) {
    running *= values.length
    projected += running
  }
  const crossProductComplete = projected <= MAX_BREAKDOWN_NODES
  if (crossProductComplete) {
    let paths: string[][] = [[]]
    for (let depth = 0; depth < valuesByLevel.length; depth++) {
      paths = paths.flatMap((prefix) => valuesByLevel[depth]!.map((v) => [...prefix, v]))
      for (const path of paths) {
        const key = path.join(PATH_SEP)
        if (!byPath.has(key)) byPath.set(key, { levelKey: supported[depth]!, path, y1: 0, y2: 0 })
      }
    }
  }

  const levelPct = trendPct(year1, year2)

  // No prior year: inherit the nearest ancestor's rate (the level's at the root); zero would claim
  // the node is flat.
  const ownPctFor = (path: string[]): number | null => {
    for (let depth = path.length; depth > 0; depth--) {
      const acc = byPath.get(path.slice(0, depth).join(PATH_SEP))
      if (acc && acc.y2 > 0) return trendPct(acc.y1, acc.y2)
    }
    return levelPct
  }

  // On the DEEPEST nodes, which partition the level exactly once. `carried` on both sides:
  // inherited units are in the effect being corrected, so they pay for it too.
  const deepest = [...byPath.values()].filter((acc) => acc.path.length === supported.length)
  let carriedEffect = 0
  let grossMovement = 0
  for (const acc of deepest) {
    const carried = ownPctFor(acc.path) ?? 0
    carriedEffect += acc.y1 * carried
    grossMovement += acc.y1 * Math.abs(carried)
  }
  const correctionRatio =
    levelPct == null || grossMovement === 0 || supported.length === 0
      ? 0
      : Math.min(1, (carriedEffect - year1 * levelPct) / grossMovement)
  // `sign(0) = 0` leaves a node carrying exactly 0 alone.
  const applyCorrection = (carried: number): number =>
    carried * (1 - correctionRatio * Math.sign(carried))
  // Raw `levelPct` is a measurement, not something to apply.
  const inheritedPct = levelPct == null ? null : applyCorrection(levelPct)

  const nodes: NodeSalesTrend[] = [...byPath.values()].map((acc) => {
    const carried = ownPctFor(acc.path)
    return {
      levelKey: acc.levelKey,
      path: acc.path,
      label: acc.path[acc.path.length - 1] || UNCATEGORIZED,
      year1: acc.y1,
      year2: acc.y2,
      pct: trendPct(acc.y1, acc.y2),
      carriedPct: carried,
      appliedPct: carried == null ? null : applyCorrection(carried),
    }
  })

  const pctByVariant: Record<string, number> = {}
  if (inheritedPct != null && wantVariant) {
    const byVariant = new Map<string, { w: number; wp: number }>()
    for (const r of rows) {
      const applied = applyCorrection(
        ownPctFor(supported.map((levelKey) => levelValueOf(r, levelKey))) ?? 0,
      )
      const acc = byVariant.get(r.variantId) ?? { w: 0, wp: 0 }
      acc.w += r.year1
      acc.wp += r.year1 * applied
      byVariant.set(r.variantId, acc)
    }
    // `inheritedPct`, not `levelPct`: must resolve the same way per variant and per node.
    for (const [variantId, acc] of byVariant) {
      pctByVariant[variantId] = acc.w > 0 ? acc.wp / acc.w : inheritedPct
    }
  }
  // Shallowest first (one-pass tree build), then siblings by DISPLAYED label, numeric collation.
  nodes.sort(
    (a, b) =>
      a.path.length - b.path.length ||
      a.path.slice(0, -1).join(PATH_SEP).localeCompare(b.path.slice(0, -1).join(PATH_SEP)) ||
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }),
  )

  // `pct` null = no prior year, so no trend rather than infinite growth; the insight isn't offered.
  return {
    year1,
    discountYear1,
    year2,
    pct: levelPct,
    inheritedPct,
    nodes,
    unsupportedLevels,
    crossProductComplete,
    correctionRatio,
    pctByVariant,
    window: { ...window, maxSaleDate },
  }
}
